import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Notice, Spinner } from '../components/ui';
import { useMe } from '../lib/session';
import { useMembers, useSchool } from '../lib/school';
import { usePeople } from '../lib/people';
import { getProfile } from '../lib/onboarding';
import { api } from '../lib/api';
import './Page.css';

const plural = (n: number, one: string, many: string) => `${n === 1 ? one : many}`;

/** Remembered in this browser so "Share the organisation code" ticks when copied. */
const copiedKey = (schoolId: string | null | undefined) => `educaitor.codeShared.${schoolId ?? ''}`;

type Step = { id: string; label: string; done: boolean; to?: string; action?: string };

export function OverviewPage() {
  const me = useMe();
  const school = useSchool();
  const members = useMembers();
  const people = usePeople();
  const profile = useQuery({ queryKey: ['schoolProfile'], queryFn: getProfile });
  const shared = useQuery({
    queryKey: ['shared'],
    queryFn: async () => (await api<{ data: { is_own: number }[] }>('/schools/mine/shared-sessions')).data,
  });
  const [codeShared] = useState(() => {
    try {
      return localStorage.getItem(copiedKey(me.school_id)) === '1';
    } catch {
      return false;
    }
  });

  const firstName = me.name.split(' ')[0];
  const list = members.data ?? [];
  const active = list.filter((m) => m.last_active_at && Date.now() - new Date(m.last_active_at).getTime() < 30 * 86_400_000).length;
  const lessons = list.reduce((sum, m) => sum + m.session_count, 0);


  // Setup checklist (N-04). Each step ticks itself when the thing happens.
  const steps: Step[] = [
    {
      id: 'invite',
      label: 'Invite your staff',
      done: (people.data?.invites.length ?? 0) > 0 || list.length > 1,
      to: '/people',
      action: 'Invite people',
    },
    { id: 'code', label: 'Share your organisation code', done: codeShared || list.length > 1, to: '/people', action: 'Find it' },
    {
      id: 'priorities',
      label: 'Choose your Leadership Priority Areas',
      done: (profile.data?.priorities.length ?? 0) > 0,
      to: '/school',
      action: 'Choose',
    },
    { id: 'session', label: 'First lesson recorded in the app', done: lessons > 0 },
    {
      id: 'shared',
      label: 'First report shared with you',
      done: (shared.data ?? []).some((r) => !Number(r.is_own)),
      to: '/shared',
    },
  ];
  const doneCount = steps.filter((s) => s.done).length;
  const orgName = school.data?.name ?? me.school_name ?? 'Your organisation';

  return (
    <div className="page">
      <header className="page-head">
        <p className="eyebrow">{orgName}</p>
        <h1>Hello, {firstName}</h1>
      </header>

      {/* Only EducAitor admins get here with setup unfinished (everyone else is
          held at setup), and only an owner can finish it. */}
      {!me.school_onboarded_at || me.school_setup_complete === false ? (
        Number(me.is_owner) || me.role === 'system_admin' ? (
          <section className="panel panel-tint setup-prompt">
            <div>
              <h2>Finish setting up {me.school_name ?? 'your organisation'}</h2>
              <p className="muted">A few quick questions, your framework and your team. About three minutes.</p>
            </div>
            <Link to="/welcome" className="btn btn-primary btn-md">
              Continue setup
            </Link>
          </section>
        ) : (
          <Notice>{me.school_name ?? 'This organisation'} hasn&rsquo;t finished setup. Only an owner can do that.</Notice>
        )
      ) : null}

      {school.isError || members.isError ? (
        <Notice tone="error">We couldn&rsquo;t load your organisation. Refresh the page to try again.</Notice>
      ) : school.isLoading || members.isLoading ? (
        <Spinner />
      ) : (
        <>
          <section className="panel">
            <div className="stat-row">
              <div className="stat">
                <span className="stat-value">{list.length}</span>
                <span className="stat-label">{plural(list.length, 'person', 'people')} in your organisation</span>
              </div>
              <div className="stat">
                <span className="stat-value">{active}</span>
                <span className="stat-label">recorded in the last 30 days</span>
              </div>
              <div className="stat">
                <span className="stat-value">{lessons}</span>
                <span className="stat-label">{plural(lessons, 'lesson', 'lessons')} recorded</span>
              </div>
            </div>
          </section>

          {doneCount < steps.length ? (
            <section className="panel checklist" aria-labelledby="checklist-title">
              <div className="checklist-head">
                <h2 id="checklist-title">Get started</h2>
                <span className="faint">
                  {doneCount} of {steps.length} done
                </span>
              </div>
              <div className="checklist-track" aria-hidden="true">
                <span style={{ width: `${(doneCount / steps.length) * 100}%` }} />
              </div>
              <ul className="checklist-list">
                {steps.map((s) => (
                  <li key={s.id} className={s.done ? 'is-done' : undefined}>
                    <span className="checklist-mark" aria-hidden="true">
                      {s.done ? '✓' : ''}
                    </span>
                    <span className="checklist-label">
                      {s.label}
                      <span className="sr-label">{s.done ? ' (done)' : ' (to do)'}</span>
                    </span>
                    {!s.done && s.to && s.action ? (
                      <Link to={s.to} className="checklist-action">
                        {s.action}
                      </Link>
                    ) : null}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          <section className="panel">
            <div className="code-row">
              <div>
                <h2>Leadership Hub</h2>
                <p className="muted">How teaching is developing across {orgName}: who&rsquo;s recording, strengths, next steps and your priority areas.</p>
              </div>
              <Link to="/hub" className="btn btn-secondary btn-md">
                Open the Hub
              </Link>
            </div>
          </section>

          <section className="panel panel-tint">
            <div className="code-row">
              <div>
                <h2>Invite your team</h2>
                <p className="muted">Each person gets an email with a button that opens the app and joins {orgName}. No code needed.</p>
              </div>
              <Link to="/people?invite=1" className="btn btn-primary btn-md">
                Invite by email
              </Link>
            </div>
          </section>

          <p className="muted">
            Manage who&rsquo;s in your organisation on the <Link to="/people">People</Link> page.
          </p>
        </>
      )}
    </div>
  );
}
